import { useId, useState } from 'react';
import { PS, T } from '../../lib/theme';
import type { StatBreakdown } from '../../lib/types';

interface BarChartProps {
  data: StatBreakdown[];
  height?: number;
  color?: string;
  /** Format the selected bar's value. */
  format?: (v: number) => string;
  /** Render a small, responsive x axis for time-series charts. */
  showXAxis?: boolean;
  /** Turns a raw data label into its visible x-axis label. */
  labelFormat?: (label: string) => string;
  /** Optional compact label for a visible axis while keeping readouts exact. */
  axisLabelFormat?: (label: string) => string;
  /** Accessible description for the series. */
  ariaLabel?: string;
}

/**
 * Hand-rolled SVG vertical bar chart with an in-flow readout. Keeping the
 * readout below the plot instead of positioning a floating tooltip prevents it
 * from escaping a narrow viewport, while every full-height hit target remains
 * usable even when its visual bar is zero pixels high.
 */
export function BarChart({
  data,
  height = 120,
  color = PS.teal,
  format,
  showXAxis = false,
  labelFormat = (label) => label,
  axisLabelFormat = labelFormat,
  ariaLabel = 'trend chart',
}: BarChartProps) {
  const [selection, setSelection] = useState<{ data: StatBreakdown[]; index: number } | null>(null);
  const readoutId = useId();

  if (data.length === 0) {
    return <div style={{ ...T.caption }}>No data for this period.</div>;
  }

  const max = Math.max(...data.map((d) => d.value), 1);
  // Dense series keep a 28px hit target and scroll horizontally rather than
  // compressing every date into an unusable fraction of the card width.
  const barSlotWidth = 32;
  const chartWidth = Math.max(100, data.length * barSlotWidth);
  const barWidth = chartWidth / data.length;
  const gap = 4;
  const plotHeight = height;
  // Tie the index to the exact series it belongs to. This invalidates stale
  // readouts during render, without a passive reset that can race a new hover.
  const selectedIndex = selection?.data === data ? selection.index : null;
  const selected = selectedIndex === null ? null : data[selectedIndex];
  const formatValue = (value: number) => (format ? format(value) : value.toLocaleString('en-US'));
  const select = (index: number) => setSelection({ data, index });
  const axisIndexes = data.length <= 5
    ? data.map((_, index) => index)
    : [...new Set([0, Math.floor((data.length - 1) / 4), Math.floor((data.length - 1) / 2), Math.floor((data.length - 1) * 0.75), data.length - 1])];

  return (
    <div data-testid="bar-chart" style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div data-testid="bar-chart-scroll" style={{ maxWidth: '100%', overflowX: 'auto' }}>
        <div style={{ minWidth: '100%', width: chartWidth }}>
          <svg
            width="100%"
            height={height}
            viewBox={`0 0 ${chartWidth} ${height}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={ariaLabel}
          >
            {data.map((d, i) => {
              const h = Math.max((d.value / max) * Math.max(plotHeight - 2, 1), d.value === 0 ? 1 : 0);
              const x = i * barWidth + gap / 2;
              const label = `${labelFormat(d.label)}: ${formatValue(d.value)}`;
              return (
                <g
                  key={`${d.label}-${i}`}
                  role="button"
                  tabIndex={0}
                  aria-label={label}
                  aria-describedby={readoutId}
                  aria-pressed={selectedIndex === i}
                  onPointerEnter={() => select(i)}
                  onPointerDown={() => select(i)}
                  onFocus={() => select(i)}
                  onClick={() => select(i)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      select(i);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <rect
                    data-hit-target="true"
                    x={x}
                    y={0}
                    width={barWidth - gap}
                    height={plotHeight}
                    fill="transparent"
                  />
                  <rect
                    x={x}
                    y={plotHeight - h}
                    width={barWidth - gap}
                    height={h}
                    fill={color}
                    rx={0.4}
                    stroke={selectedIndex === i ? PS.nearBlack : 'none'}
                    strokeWidth={selectedIndex === i ? 0.8 : 0}
                  />
                </g>
              );
            })}
          </svg>
          {showXAxis && (
            <div
              aria-hidden="true"
              style={{
                color: PS.textSec,
                fontSize: 11,
                lineHeight: '14px',
                minWidth: chartWidth,
                position: 'relative',
                height: 14,
                whiteSpace: 'nowrap',
              }}
            >
              {axisIndexes.map((index) => (
                <span
                  key={index}
                  style={{
                    left: `${((index + 0.5) / data.length) * 100}%`,
                    position: 'absolute',
                    transform: 'translateX(-50%)',
                  }}
                >
                  {axisLabelFormat(data[index].label)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div
        id={readoutId}
        role="status"
        aria-live="polite"
        style={{ ...T.caption, color: PS.nearBlack, minHeight: 20 }}
      >
        {selected ? `${labelFormat(selected.label)}: ${formatValue(selected.value)}` : 'Select a bar to read its exact value.'}
      </div>
    </div>
  );
}
